/**
 * Iframe Sync Handler
 *
 * Handles SSO token synchronization via postMessage from central auth app
 * Renders a hidden iframe to maintain session sync with central auth
 */

import { useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { centralSyncFrameUrl, getCentralAppOrigin, getCentralAppUrl } from '../../config/authDomain';

export const IframeSyncHandler: React.FC = () => {
  const { login, logout } = useAuthStore();
  const centralAppUrl = getCentralAppUrl();
  const allowedOrigin = getCentralAppOrigin();

  useEffect(() => {
    if (!allowedOrigin) {
      // No central SSO configured (local dev / standalone); the dev-auth-helper
      // paste-token flow handles auth instead.
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      // Security: Verify origin
      if (event.origin !== allowedOrigin) {
        console.warn('Received postMessage from untrusted origin:', event.origin);
        return;
      }

      const { type, token, accessToken, refreshToken, action } = event.data;

      // Handle auth token from SSO (supports both formats)
      if (type === 'auth' && (token || accessToken)) {
        login({
          accessToken: accessToken || token,
          refreshToken: refreshToken,
        });
      }

      // Handle logout action
      if (type === 'auth' && action === 'logout') {
        logout();
      }

      // Handle token sync
      if (type === 'token-sync' && (token || accessToken)) {
        login({
          accessToken: accessToken || token,
          refreshToken: refreshToken,
        });
      }

      // Handle SESSION_SYNC (from authentication guide pattern)
      if (type === 'SESSION_SYNC' && (token || accessToken)) {
        login({
          accessToken: accessToken || token,
          refreshToken: refreshToken,
        });
      }
    };

    // Listen for postMessage events
    window.addEventListener('message', handleMessage);

    // Request initial token sync from central app (if in iframe)
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: 'request-token', source: 'image-lab' },
        allowedOrigin
      );
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [login, logout, allowedOrigin]);

  if (!centralAppUrl) return null;

  return (
    <iframe
      src={centralSyncFrameUrl(centralAppUrl)}
      style={{ display: 'none' }}
      title="Auth Sync"
      sandbox="allow-scripts allow-same-origin"
    />
  );
};
